// NetworkTypesList.tsx
import { DeleteConfirmationDialog } from "@shared/components/deleteComformationModal";
import { toast } from "@shared/hooks/use-toast";
import { Button } from "@shared/ui/button";
import { Card } from "@shared/ui/card";
import { Dialog, DialogContent, DialogTrigger } from "@shared/ui/dialog";
import { Input } from "@shared/ui/input";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { Edit, Plus, Search, Settings, Trash2 } from "lucide-react";
import { useState } from "react";
import {
  useAddNetworkType,
  useDeleteNetworkType,
  useNetworkType,
  useSearchNetwokType,
  useUpdateNetworkType,
} from "../../hooks";
import { NetworkTypeForm } from "../create/create";
import { logEvent } from "@shared/analytics/analytics";

interface NetworkType {
  id: string;
  name: string;
  description: string;
  createdAt: string;
}

export function NetworkTypesList() {
  const remoteUtilityId = getRemoteUtilityId();
  const { data: networkData, refetch } = useNetworkType({
    remote_utility_id: remoteUtilityId,
    config_level: "network_type",
    show_inactive: "true",
  });

  const addNetworkMutation = useAddNetworkType();
  const updateNetworkMutation = useUpdateNetworkType();
  const deleteNetworkMutation = useDeleteNetworkType();
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<NetworkType | null>(null);
  const [addForm, setAddForm] = useState({ name: "", description: "" });
  const [editForm, setEditForm] = useState({ name: "", description: "" });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [networkToDelete, setNetworkToDelete] = useState<NetworkType | null>(
    null
  );

  const { data: searchData, refetch: refetchSearch } = useSearchNetwokType({
    remote_utility_id: remoteUtilityId,
    search_data: searchTerm,
    search_field: "network_type",
  });

  // Use search results if search term exists, otherwise use all data
  const currentData = searchTerm ? searchData : networkData;
  const networkTypes: NetworkType[] =
    currentData?.result?.map((item: any) => ({
      id: item.code,
      name: item.name,
      description: item.description || "",
      createdAt: item.createdDate,
    })) || [];

  const handleSaveAdd = async () => {
    if (!addForm.name) return;

    try {
      await addNetworkMutation.mutateAsync({
        name: addForm.name,
        code: addForm.name,
        remote_utility_id: remoteUtilityId,
        description: addForm.description,
      });
      logEvent("network_type_added", { name: addForm.name });
      setAddForm({ name: "", description: "" });
      setIsAddDialogOpen(false);
      refetch();
      refetchSearch();

      toast({
        title: "Success",
        description: "Network type added successfully",
      });
    } catch (error) {
      logEvent("network_type_add_failed", { error: error.message });
      toast({
        title: "Error",
        description: `Failed to add network type: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  const handleSaveEdit = async () => {
    if (!editForm.name || !editingType) return;

    try {
      await updateNetworkMutation.mutateAsync({
        name: editForm.name,
        code: editingType.id,
        remote_utility_id: remoteUtilityId,
        description: editForm.description,
      });
      logEvent("network_type_updated", {
        id: editingType.id,
        name: editForm.name,
      });
      setEditForm({ name: "", description: "" });
      setEditingType(null);
      setEditDialogOpen(false);
      refetch();

      toast({
        title: "Success",
        description: "Network type updated successfully",
      });
    } catch (error) {
      logEvent("network_type_update_failed", { id: editingType?.id });
      toast({
        title: "Error",
        description: "Failed to update network type",
        variant: "destructive",
      });
    }
  };

  const handleStartEdit = (type: NetworkType) => {
    setEditingType(type);
    setEditForm({
      name: type.name,
      description: type.description,
    });
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (type: NetworkType) => {
    setNetworkToDelete(type);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!networkToDelete) return;

    try {
      await deleteNetworkMutation.mutateAsync({
        remote_utility_id: remoteUtilityId,
        code: networkToDelete.id,
      });
      logEvent("network_type_deleted", {
        id: networkToDelete.id,
        name: networkToDelete.name,
      });
      refetch();

      toast({
        title: "Success",
        description: "Network type deleted successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete network type",
        variant: "destructive",
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setNetworkToDelete(null);
    }
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setNetworkToDelete(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Search network types..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={() => {
                logEvent("network_type_add_button_clicked");
                setAddForm({ name: "", description: "" });
                setIsAddDialogOpen(true);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Network Type
            </Button>
          </DialogTrigger>
          <DialogContent>
            <NetworkTypeForm
              mode="add"
              formData={addForm}
              onFormChange={setAddForm}
              onSave={handleSaveAdd}
              onCancel={() => {
                setIsAddDialogOpen(false);
                setAddForm({ name: "", description: "" });
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      <DeleteConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        title="Delete Network Type"
        itemName={networkToDelete?.name}
        description={`Are you sure you want to delete the network type "${networkToDelete?.name}"? This action cannot be undone and may affect related configurations.`}
        confirmText="Delete Network Type"
        isLoading={deleteNetworkMutation.isPending}
      />

      {networkTypes.length === 0 && (
        <Card className="p-8">
          <div className="text-center">
            <p className="text-gray-500">
              {searchTerm ? (
                <div className="text-center py-6 border rounded-md bg-muted/30">
                  <Settings className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
                  <h3 className="font-medium mb-1">No Network Types</h3>
                  <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
                    No network types found matching "{searchTerm}". Try
                    adjusting your search or create a new network type.
                  </p>
                  <Button
                    onClick={() => {
                      setAddForm({ name: "", description: "" });
                      setIsAddDialogOpen(true);
                    }}
                    className="ml-4"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Network Type
                  </Button>
                </div>
              ) : (
                "No network types found"
              )}
            </p>
          </div>
        </Card>
      )}

      <div className="space-y-4">
        {networkTypes.map((type) => (
          <Card key={type.id} className="p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between w-full">
              <div className="flex flex-col items-start gap-2 flex-1">
                <div className="flex items-center gap-3">
                  <span className="text-lg font-semibold text-blue-600">
                    {type.name}
                  </span>
                </div>
                {type.description && (
                  <div className="text-left w-full">
                    <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md border-l-4 border-blue-200">
                      <span className="font-medium text-gray-800">
                        Description:{" "}
                      </span>
                      {type.description}
                    </p>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Dialog
                  open={editDialogOpen && editingType?.id === type.id}
                  onOpenChange={setEditDialogOpen}
                >
                  <DialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleStartEdit(type)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <NetworkTypeForm
                      mode="edit"
                      formData={editForm}
                      onFormChange={setEditForm}
                      onSave={handleSaveEdit}
                      onCancel={() => {
                        setEditDialogOpen(false);
                        setEditForm({ name: "", description: "" });
                        setEditingType(null);
                      }}
                    />
                  </DialogContent>
                </Dialog>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteClick(type)}
                  disabled={true}
                  tooltip={"This functionality is currently not available."}
                  tooltipPosition="left"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
